import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Shield,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Loader2,
  CheckCircle2,
  MessageSquare,
  Mic
} from 'lucide-react';
import VoiceInput from '../components/VoiceInput';

const DiscoveryPage = () => {
  const { templateId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [step, setStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [answers, setAnswers] = useState([]);

  useEffect(() => {
    startDiscovery();
  }, [templateId]);

  const startDiscovery = async () => {
    setLoading(true);
    try {
      const response = await api.post('/discovery/start', {
        template_type: templateId || null
      });
      setSessionId(response.data.session_id);
      setCurrentQuestion(response.data.question);
      setStep(response.data.step);
      setTotalSteps(response.data.total_steps);
      setIsComplete(response.data.is_complete);
    } catch (error) {
      console.error('Error starting discovery:', error);
      toast.error('Error al iniciar la entrevista');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (!answer.trim()) {
      toast.error('Por favor, responde la pregunta');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/discovery/next', {
        session_id: sessionId,
        answer: answer.trim()
      });
      
      setAnswers([...answers, { question: currentQuestion, answer: answer.trim() }]);
      setCurrentQuestion(response.data.question);
      setStep(response.data.step);
      setIsComplete(response.data.is_complete);
      setAnswer('');
    } catch (error) {
      console.error('Error proceeding:', error);
      toast.error('Error al procesar la respuesta');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/contracts/generate', {
        session_id: sessionId
      });
      
      const jobId = response.data.job_id;
      toast.success('Generando contrato...');
      
      // Poll for job completion
      const pollJob = async () => {
        try {
          const jobResponse = await api.get(`/jobs/${jobId}`);
          const job = jobResponse.data;
          
          if (job.status === 'completed') {
            toast.success('¡Contrato generado exitosamente!');
            navigate(`/contract/${job.result.contract_id}`);
          } else if (job.status === 'failed') {
            toast.error('Error al generar el contrato');
            setGenerating(false);
          } else {
            setTimeout(pollJob, 2000);
          }
        } catch (error) {
          console.error('Error polling job:', error);
          setGenerating(false);
        }
      };
      
      pollJob();
    } catch (error) {
      console.error('Error generating contract:', error);
      toast.error('Error al iniciar la generación');
      setGenerating(false);
    }
  };

  const progress = totalSteps > 0 ? (step / totalSteps) * 100 : 0;

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Header */}
      <header className="glass border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center space-x-3 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Volver</span>
            </Link>
            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6 text-blue-500" />
              <span className="font-semibold font-['Outfit']">Asistente de Contratos</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-12">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between text-sm text-slate-400 mb-2">
            <span>Paso {step} de {totalSteps}</span>
            <span>{Math.round(progress)}% completado</span>
          </div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-600 to-cyan-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {loading && !generating ? (
          <div className="text-center py-20">
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-blue-500 mb-4" />
            <p className="text-slate-400">Procesando...</p>
          </div>
        ) : generating ? (
          <div className="text-center py-20 animate-fade-in">
            <div className="w-20 h-20 mx-auto mb-6 relative">
              <div className="absolute inset-0 bg-blue-500/20 rounded-full animate-ping"></div>
              <div className="relative w-full h-full bg-blue-500/10 rounded-full flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-blue-400 animate-pulse" />
              </div>
            </div>
            <h2 className="text-2xl font-semibold font-['Outfit'] mb-3">Generando tu contrato</h2>
            <p className="text-slate-400 mb-2">Nuestra IA está redactando un documento legal personalizado...</p>
            <p className="text-sm text-slate-500">Esto puede tomar unos segundos</p>
          </div>
        ) : isComplete ? (
          <div className="text-center py-12 animate-slide-up">
            <div className="w-20 h-20 mx-auto mb-6 bg-emerald-500/10 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-semibold font-['Outfit'] mb-3">¡Entrevista Completada!</h2>
            <p className="text-slate-400 mb-8">
              Hemos recopilado toda la información necesaria. 
              Ahora podemos generar tu contrato personalizado.
            </p>

            {/* Summary of answers */}
            <div className="bg-card border border-border rounded-xl p-6 text-left mb-8 max-h-60 overflow-y-auto">
              <h3 className="text-sm font-medium text-slate-400 mb-4">Resumen de respuestas:</h3>
              <div className="space-y-3">
                {answers.map((item, index) => (
                  <div key={index} className="text-sm">
                    <p className="text-slate-500">{item.question}</p>
                    <p className="text-slate-300">{item.answer}</p>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={handleGenerate}
              data-testid="generate-contract-btn"
              className="btn-primary px-8 py-3 text-lg inline-flex items-center space-x-2"
            >
              <Sparkles className="w-5 h-5" />
              <span>Generar Contrato</span>
            </button>
          </div>
        ) : (
          <div className="animate-fade-in">
            {/* Question Card */}
            <div className="bg-card border border-border rounded-xl p-8 mb-6">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400 mb-1">Pregunta {step}</p>
                  <h2 className="text-xl font-medium">{currentQuestion}</h2>
                </div>
              </div>

              <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                data-testid="discovery-answer-input"
                placeholder="Escribe o dicta tu respuesta aquí..."
                rows={4}
                className="w-full bg-slate-900/50 border border-slate-700 rounded-lg p-4 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all resize-none text-white placeholder-slate-500"
              />
              <div className="flex items-center gap-2 mt-3">
                <VoiceInput 
                  onTranscription={(text) => setAnswer(prev => prev ? `${prev} ${text}` : text)}
                  disabled={loading}
                />
                <span className="text-sm text-slate-500">
                  Usa el micrófono para dictar tu respuesta
                </span>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleNext}
                disabled={!answer.trim()}
                data-testid="discovery-next-btn"
                className="btn-primary px-6 py-3 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Continuar</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DiscoveryPage;
